import { Cause, Effect, Exit, Layer, Ref, Schema, Scope, Semaphore } from "effect";
import { Atom, AtomRegistry } from "effect/unstable/reactivity";
import { INTERNAL_HTTP_MESSAGE } from "@workerstation/shared/errors/http";
import { AcceptedWork } from "../accepted-work.ts";
import { makeSessionOwner } from "../session-owner.ts";
import { frontendThemeFailure, getTheme, themeCss, ThemeScopeId, type ThemeSnapshot } from "./theme-model.ts";
import { FrontendThemes, ThemeStyleIo, type ThemeController } from "./theme-service.ts";

export const FrontendThemesLive = (root: Scope.Scope) => Layer.effect(FrontendThemes, Effect.gen(function* () {
  const io = yield* ThemeStyleIo, accepted = yield* AcceptedWork, registry = yield* AtomRegistry.AtomRegistry, resources = yield* Scope.Scope;
  const failures = yield* Ref.make<readonly Cause.Cause<never>[]>([]);
  yield* Effect.addFinalizer(() => Ref.get(failures).pipe(Effect.flatMap(values => values.length
    ? Effect.failCause(Cause.fromReasons(values.flatMap(value => value.reasons))) : Effect.void)));
  const sessions = yield* makeSessionOwner(root);
  const acquire = Effect.gen(function* () {
    const owner = yield* Scope.Scope;
    const lease = yield* io.connect;
    const scopeId = yield* Schema.decodeUnknownEffect(ThemeScopeId)(lease.scopeId).pipe(Effect.mapError(frontendThemeFailure));
    const state = yield* Ref.make<ThemeSnapshot>({ scopeId, themeId: "oc-2", initialized: false, error: null });
    const gate = yield* Semaphore.make(1), read = Ref.get(state), snapshot = Atom.make(read);
    const alive = () => root.state._tag !== "Closed" && owner.state._tag !== "Closed";
    const publish = Effect.sync(() => { if (alive()) registry.refresh(snapshot); });
    const set = (id: string, initial: boolean) => accepted.join(gate.withPermit(Effect.gen(function* () {
      if (!alive()) return;
      const current = yield* Ref.get(state);
      if (initial && current.initialized) return;
      if (!Schema.is(Schema.String)(id)) return;
      const outcome = yield* Effect.exit(Effect.suspend(() => lease.write(themeCss(getTheme(id), scopeId))).pipe(Effect.orDie));
      if (Exit.isFailure(outcome)) yield* Ref.update(failures, values => [...values, outcome.cause]);
      if (!alive()) return;
      yield* Ref.set(state, Exit.isSuccess(outcome) ? { scopeId, themeId: id, initialized: true, error: null } : { ...current, error: INTERNAL_HTTP_MESSAGE });
      yield* publish;
    })));
    return { snapshot, read, initialize: id => set(id, true), select: id => set(id, false) } satisfies ThemeController;
  }).pipe(Effect.orDie, Effect.onExit(exit => Exit.isFailure(exit) ? Ref.update(failures, values => [...values, exit.cause]) : Effect.void));
  const cached = yield* Effect.cached(sessions.open(acquire).pipe(Effect.provideService(Scope.Scope, resources)));
  return FrontendThemes.of({ open: Effect.suspend(() => root.state._tag === "Closed" ? Effect.interrupt : cached) });
}));
